import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, ExternalLink, Heart } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import type { CragSupportPrompt, Maintainer } from '../../types/maintainer';

interface CragSupportDialogProps {
  /** The crag and its maintainers, or null when there is nothing to ask about. */
  prompt: CragSupportPrompt | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * Where a maintainer's row goes, and what it is honest about promising.
 *
 * Their donation page first — that is the one link where the ask is already
 * made — then their home page, and only failing both our own page about them,
 * which at least says who took the money for the bolts.
 */
function supportLink(maintainer: Maintainer): { href: string; external: boolean; labelKey: string } {
  if (maintainer.donation_url) {
    return { href: maintainer.donation_url, external: true, labelKey: 'cragSupport.donate' };
  }
  if (maintainer.website_url) {
    return { href: maintainer.website_url, external: true, labelKey: 'cragSupport.visitSite' };
  }
  return { href: `/maintainers/${maintainer.slug}`, external: false, labelKey: 'cragSupport.aboutThem' };
}

/**
 * The ask that follows a send at a crag somebody looks after.
 *
 * It arrives on the send rather than on the crag's page because that is the one
 * moment the climber has just taken something from the place: the bolt they
 * clipped was paid for by whoever is listed here.
 *
 * Every row is a link out, as deep as the club has let us go — see
 * `supportLink`. The point is to leave.
 *
 * Nothing is asked twice in a day: see `crag-support-prompt.ts` for why, and
 * for who decides it (the caller, before this is ever rendered).
 */
export function CragSupportDialog({ prompt, onOpenChange }: CragSupportDialogProps) {
  const { t } = useTranslation();

  if (!prompt) return null;

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
        <DialogHeader className="text-left">
          <DialogTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-red-500" />
            {t('cragSupport.title', { name: prompt.crag.name })}
          </DialogTitle>
          <DialogDescription>
            {t('cragSupport.description', { name: prompt.crag.name })}
          </DialogDescription>
        </DialogHeader>

        <ul className="-mx-6 divide-y border-y">
          {prompt.maintainers.map((maintainer) => {
            const { href, external, labelKey } = supportLink(maintainer);
            const logo = maintainer.logo_small_url || maintainer.logo_url;
            const rowClass = 'flex items-center gap-3 px-6 py-3 transition-colors hover:bg-accent';
            const content = (
              <>
                <Avatar className="h-10 w-10">
                  {logo && (
                    <AvatarImage src={logo} alt={maintainer.name} className="object-contain" />
                  )}
                  <AvatarFallback className="text-xs">
                    {maintainer.name.slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{maintainer.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">{t(labelKey)}</span>
                </span>
                {external
                  ? <ExternalLink className="h-4 w-4 shrink-0 text-muted-foreground" />
                  : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
              </>
            );

            return (
              <li key={maintainer.id}>
                {external ? (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => onOpenChange(false)}
                    className={rowClass}
                  >
                    {content}
                  </a>
                ) : (
                  <Link to={href} onClick={() => onOpenChange(false)} className={rowClass}>
                    {content}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>

        <DialogFooter>
          {/* No "donate" button of its own: the rows above are the donation,
              and a second call to action would only be a way of asking twice. */}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('cragSupport.dismiss')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
